import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { COMPANY } from "endpointsConst";
import { LOADING, SUCCEEDED, FAILED, IDLE } from "constants/fetch";

const initialState = {
    companyData: {},
    logo: "",
    error: null,
    status: IDLE,
};

export const getCompanyInfo = createAsyncThunk(
    "company/get",
    async ({ id, lang }: { id: string; lang: string }, { rejectWithValue }) => {
        try {
            const res = await axios.get(COMPANY.GET + id, {
                params: {
                    lang,
                },
            });
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.status || 500);
        }
    },
);

export const ALLOWED_COMPANY_FIELDS = [
    'name',
    'payer_number',
    'legal_address',
    'post_address',
    'phone',
    'email',
    'site',
    'creation_date',
    'legal_form',
    // company_roles здесь нет намеренно: тип компании задаётся при регистрации,
    // меняет его администратор по заявке из профиля
] as const;

export type AllowedCompanyField = (typeof ALLOWED_COMPANY_FIELDS)[number];

export interface CompanyPatchParams {
    name: string;
    payer_number: number;
    legal_address?: string | null;
    post_address?: string | null;
    phone?: string | null;
    email?: string | null;
    site?: string | null;
    creation_date?: string | Date | null;
    legal_form?: string | null;
}

export function sanitizeCompanyParams(params: Record<string, unknown>): Partial<CompanyPatchParams> {
    const sanitized: Partial<CompanyPatchParams> = {};
    ALLOWED_COMPANY_FIELDS.forEach((field) => {
        if (field in params) {
            (sanitized as Record<string, unknown>)[field] = params[field];
        }
    });
    sanitized.creation_date = sanitized.creation_date || null;
    return sanitized;
}

interface UpdateCompanyPayload {
    id: string;
    params: Record<string, unknown>;
}

export const updateCompanyInfo = createAsyncThunk(
    "company/patch",
    async (data: UpdateCompanyPayload, { rejectWithValue }) => {
        try {
            // company_roles приходит из params как есть: тип компании форма не меняет,
            // но в стор его вернуть надо — иначе профиль потеряет тип до перезагрузки
            const companyData = {
                ...data.params,
                id: data.id,
            };
            const sanitizedParams = sanitizeCompanyParams(data.params);

            await axios.patch(COMPANY.PATCH + data.id, { id: data.id, ...sanitizedParams });

            return companyData;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const companySlice = createSlice({
    name: "company",
    initialState,
    reducers: {
        updateLogo: (state, action) => {
            state.logo = action.payload;
        },
        removeCompanyData: (state) => {
            state.companyData = {};
            state.logo = "";
            state.error = null;
            state.status = IDLE;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCompanyInfo.fulfilled, (state, action) => {
                state.logo = action.payload.logo;
                state.status = SUCCEEDED;
                state.companyData = action.payload;
            })
            .addCase(getCompanyInfo.pending, (state) => {
                state.status = LOADING;
            })
            .addCase(getCompanyInfo.rejected, (state, action) => {
                state.status = FAILED;
                state.error = action.payload;
            })
            .addCase(updateCompanyInfo.fulfilled, (state, action) => {
                const { logo, ...data } = action.payload as any;
                state.companyData = data;
            })
            .addCase(updateCompanyInfo.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { updateLogo, removeCompanyData } = companySlice.actions;

export default companySlice.reducer;
